import React, { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import { useLoader } from "../../../context/LoaderContext";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import { useUserData } from "../../../hooks/useUserData";
import { getQmsAccessState } from "./qmsPermissionUtils";
import QmsQuestionBankPanel from "./QmsQuestionBankPanel";
import QmsQuizBuilderPanel from "./QmsQuizBuilderPanel";
import "./QmsPages.scss";

const TAB_QUESTIONS = "questions";
const TAB_QUIZZES = "quizzes";

const QmsQuizManager = () => {
  const { userData, loading: userLoading } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const [searchParams, setSearchParams] = useSearchParams();
  const [questionBankRevision, setQuestionBankRevision] = useState(0);
  const [actionsSlot, setActionsSlot] = useState(null);

  const isSystemAdmin = userData?.Role === "System Administrator";

  const access = getQmsAccessState({
    userData,
    isSystemAdmin,
    permissionKey: "QMS_QUIZ_MANAGER",
    loading: userLoading,
  });

  const activeTab = useMemo(() => {
    const tab = (searchParams.get("tab") || TAB_QUIZZES).toLowerCase();
    return tab === TAB_QUESTIONS ? TAB_QUESTIONS : TAB_QUIZZES;
  }, [searchParams]);

  const setActiveTab = useCallback(
    (tab) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tab === TAB_QUIZZES) {
            next.delete("tab");
          } else {
            next.set("tab", tab);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleQuestionsChanged = useCallback(() => {
    setQuestionBankRevision((n) => n + 1);
  }, []);

  const panelProps = {
    userData,
    showLoader,
    hideLoader,
    showAlert,
    actionsSlot,
  };

  if (access.status === "loading") return null;

  return (
    <div className="user-mgmt-page qms-quiz-hub qms-document-library">
      <InfoAlert isOpen={alertState.isOpen} onClose={hideAlert} {...alertState} />
      <PageBreadcrumb
        items={[
          { label: "Home", path: "/notification-center" },
          { label: "Quiz Manager" },
        ]}
        trailingExtras={
          access.status === "allowed" ? (
            <div className="qms-quiz-hub__breadcrumb-actions" ref={setActionsSlot} />
          ) : null
        }
      />

      {access.status === "denied" && (
        <div className="alert alert-warning mt-3" role="alert">
          <strong>Access restricted.</strong> {access.message}
        </div>
      )}

      {access.status === "allowed" && (
        <>
          <div className="qms-quiz-hub__tabs" role="tablist" aria-label="Quiz manager sections">
            <button
              type="button"
              role="tab"
              className={`qms-quiz-hub__tab ${activeTab === TAB_QUIZZES ? "is-active" : ""}`}
              aria-selected={activeTab === TAB_QUIZZES}
              onClick={() => setActiveTab(TAB_QUIZZES)}
            >
              <i className="fa fa-list-ol" aria-hidden="true" />
              <span>Quizzes</span>
            </button>
            <button
              type="button"
              role="tab"
              className={`qms-quiz-hub__tab ${activeTab === TAB_QUESTIONS ? "is-active" : ""}`}
              aria-selected={activeTab === TAB_QUESTIONS}
              onClick={() => setActiveTab(TAB_QUESTIONS)}
            >
              <i className="fa fa-question-circle" aria-hidden="true" />
              <span>Question master</span>
            </button>
          </div>

          <div className="qms-quiz-hub__content">
            {activeTab === TAB_QUIZZES ? (
              <QmsQuizBuilderPanel
                {...panelProps}
                questionBankRevision={questionBankRevision}
                onGoToQuestionsTab={() => setActiveTab(TAB_QUESTIONS)}
              />
            ) : (
              <QmsQuestionBankPanel
                {...panelProps}
                onQuestionsChanged={handleQuestionsChanged}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default QmsQuizManager;
